'use client';

import { useState, useEffect } from 'react';
import Navigation from './components/Navigation';
import {
  HeroSection,
  ClientLogoStrip,
  ProblemSection,
  FounderSection,
  ServicesSection,
  FrameworkSection,
  ImpactGallerySection,
  Footer,
} from './components/sections';

/**
 * Homepage.
 *
 * Deliberately an inert mount list. #7920 and #7921 both fill sections on this
 * page, and if either had to edit this file they would collide. Three
 * properties keep that from happening: sections take no props, they are
 * imported from the barrel in IA order, and SECTION_IDS is final. Adding real
 * content to a section is therefore a change to that section's own file only.
 */

// Scroll-spy targets, in document order. Drives the header's active state.
// ClientLogoStrip and the footer are not targets and have no id.
const SECTION_IDS = ['hero', 'founder', 'services', 'framework', 'impact'] as const;

export default function Home() {
  const [currentSection, setCurrentSection] = useState<string>('hero');

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;

      for (const sectionId of SECTION_IDS) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setCurrentSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative min-h-screen bg-sand">
      <Navigation currentSection={currentSection} />

      <main className="relative z-10 pt-[72px] md:pt-[78px]">
        <HeroSection />
        <ClientLogoStrip />
        <ProblemSection />
        <FounderSection />
        <ServicesSection />
        <FrameworkSection />
        <ImpactGallerySection />
      </main>

      <Footer />
    </div>
  );
}
